<script lang="ts">
  /**
   * ParticleSystem - Canvas-based particle effects for delightful feedback
   * Supports burst, confetti, and sparkle effects
   */

  import { onMount } from 'svelte';

  // Particle interface
  interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    color: string;
    alpha: number;
    lifetime: number;
    age: number;
    rotation: number;
    rotationSpeed: number;
    type: 'star' | 'confetti' | 'sparkle';
    active: boolean;
  }

  let canvas: HTMLCanvasElement;
  let ctx: CanvasRenderingContext2D | null = null;
  let particles: Particle[] = [];
  let animationId: number;

  // Particle pool for performance
  const MAX_PARTICLES = 100;
  const PARTICLE_POOL: Particle[] = [];

  // Initialize particle pool
  for (let i = 0; i < MAX_PARTICLES; i++) {
    PARTICLE_POOL.push(createParticle(0, 0, 'star'));
  }

  onMount(() => {
    ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Start animation loop
    animationId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  });

  function resizeCanvas() {
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  }

  // Create a new particle
  function createParticle(x: number, y: number, type: 'star' | 'confetti' | 'sparkle'): Particle {
    return {
      x,
      y,
      vx: 0,
      vy: 0,
      size: 0,
      color: '#FCD34D',
      alpha: 1,
      lifetime: 800,
      age: 0,
      rotation: 0,
      rotationSpeed: 0,
      type,
      active: false
    };
  }

  // Get particle from pool
  function getParticle(x: number, y: number, type: 'star' | 'confetti' | 'sparkle'): Particle | null {
    const particle = PARTICLE_POOL.find(p => !p.active);
    if (!particle) return null;

    particle.x = x;
    particle.y = y;
    particle.type = type;
    particle.age = 0;
    particle.active = true;
    return particle;
  }

  // Create success burst (8-12 star particles radial explosion)
  export function createBurst(x: number, y: number) {
    const particleCount = 8 + Math.floor(Math.random() * 5); // 8-12 particles
    const colors = ['#FCD34D', '#FFFFFF', '#93C5FD', '#FBBF24'];

    for (let i = 0; i < particleCount; i++) {
      const particle = getParticle(x, y, 'star');
      if (!particle) break;

      // Radial explosion
      const angle = (Math.PI * 2 * i) / particleCount + (Math.random() - 0.5) * 0.5;
      const speed = 100 + Math.random() * 100; // pixels per second

      particle.vx = Math.cos(angle) * speed;
      particle.vy = Math.sin(angle) * speed;
      particle.size = 8 + Math.random() * 8; // 8-16px
      particle.color = colors[Math.floor(Math.random() * colors.length)];
      particle.alpha = 1;
      particle.lifetime = 800;
      particle.rotation = Math.random() * Math.PI * 2;
      particle.rotationSpeed = (Math.random() - 0.5) * 10;
    }
  }

  // Create confetti effect (continuous falling particles)
  export function createConfetti(duration: number = 3000) {
    const startTime = performance.now();
    const colors = ['#EF4444', '#F97316', '#FBBF24', '#22C55E', '#3B82F6', '#A855F7'];

    const confettiInterval = setInterval(() => {
      if (performance.now() - startTime > duration) {
        clearInterval(confettiInterval);
        return;
      }

      // Spawn 2-3 confetti pieces
      for (let i = 0; i < 2 + Math.random(); i++) {
        const particle = getParticle(Math.random() * window.innerWidth, -20, 'confetti');
        if (!particle) continue;

        particle.vx = (Math.random() - 0.5) * 50;
        particle.vy = 50 + Math.random() * 100; // Fall speed
        particle.size = 6 + Math.random() * 6;
        particle.color = colors[Math.floor(Math.random() * colors.length)];
        particle.alpha = 1;
        particle.lifetime = 4000;
        particle.rotation = Math.random() * Math.PI * 2;
        particle.rotationSpeed = (Math.random() - 0.5) * 8;
      }
    }, 100); // Spawn every 100ms
  }

  // Create sparkle effect
  export function createSparkle(x: number, y: number) {
    const particleCount = 3 + Math.floor(Math.random() * 3); // 3-5 sparkles

    for (let i = 0; i < particleCount; i++) {
      const particle = getParticle(x + (Math.random() - 0.5) * 20, y + (Math.random() - 0.5) * 20, 'sparkle');
      if (!particle) break;

      particle.vx = (Math.random() - 0.5) * 30;
      particle.vy = (Math.random() - 0.5) * 30;
      particle.size = 3 + Math.random() * 5;
      particle.color = '#FFFFFF';
      particle.alpha = 1;
      particle.lifetime = 200; // Short-lived
      particle.rotation = 0;
      particle.rotationSpeed = 0;
    }
  }

  // Animation loop
  function animate(currentTime: number) {
    if (!ctx || !canvas) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Update and draw particles
    const deltaTime = 1 / 60; // Assume 60 FPS for physics

    PARTICLE_POOL.forEach(particle => {
      if (!particle.active) return;

      // Update age
      particle.age += deltaTime * 1000; // Convert to ms

      // Deactivate if lifetime exceeded
      if (particle.age >= particle.lifetime) {
        particle.active = false;
        return;
      }

      // Physics update
      particle.x += particle.vx * deltaTime;
      particle.y += particle.vy * deltaTime;

      // Gravity (only for burst and confetti)
      if (particle.type !== 'sparkle') {
        particle.vy += 300 * deltaTime; // Gravity acceleration
      }

      // Friction
      particle.vx *= 0.98;

      // Rotation
      particle.rotation += particle.rotationSpeed * deltaTime;

      // Fade out
      particle.alpha = 1 - (particle.age / particle.lifetime);

      // Draw particle
      ctx!.save();
      ctx!.globalAlpha = particle.alpha;
      ctx!.translate(particle.x, particle.y);
      ctx!.rotate(particle.rotation);

      if (particle.type === 'star') {
        drawStar(ctx!, particle.size, particle.color);
      } else if (particle.type === 'confetti') {
        drawConfetti(ctx!, particle.size, particle.color);
      } else {
        drawSparkle(ctx!, particle.size, particle.color);
      }

      ctx!.restore();
    });

    animationId = requestAnimationFrame(animate);
  }

  // Draw a star particle
  function drawStar(ctx: CanvasRenderingContext2D, size: number, color: string) {
    ctx.fillStyle = color;
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2;
      const x = Math.cos(angle) * size;
      const y = Math.sin(angle) * size;
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
      // Inner point
      const innerAngle = angle + Math.PI / 5;
      const innerX = Math.cos(innerAngle) * (size / 2);
      const innerY = Math.sin(innerAngle) * (size / 2);
      ctx.lineTo(innerX, innerY);
    }
    ctx.closePath();
    ctx.fill();
  }

  // Draw a confetti piece
  function drawConfetti(ctx: CanvasRenderingContext2D, size: number, color: string) {
    ctx.fillStyle = color;
    ctx.fillRect(-size / 2, -size / 2, size, size * 1.5);
  }

  // Draw a sparkle
  function drawSparkle(ctx: CanvasRenderingContext2D, size: number, color: string) {
    ctx.fillStyle = color;
    ctx.shadowBlur = 10;
    ctx.shadowColor = color;
    ctx.beginPath();
    ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
  }
</script>

<canvas
  bind:this={canvas}
  class="particle-canvas"
></canvas>

<style>
  .particle-canvas {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    pointer-events: none;
    z-index: 9999; /* Above everything */
  }
</style>
